import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from 'lucide-react';
import type { FilterField, ActiveFilters } from './FilterBar';
// danh sách dùng hàng lọc cột khai báo cột lọc bằng kiểu này (LayoutList, WasteMatrixList…)
export type { FilterField } from './FilterBar';
import { SearchableFilterDropdown } from '@/components/shared/SearchableFilterDropdown';

// ── Types ────────────────────────────────────────────────────────────────────

export interface ColumnFilter {
    /** Field key matching FilterField.key */
    key: string;
    /** Width for the filter cell (should match th width) */
    width?: string;
    /** Mark this null cell as the checkbox column so it gets apv-col-checkbox class */
    isCheckboxCol?: boolean;
}

/** Sentinel object to mark the checkbox column null slot */
export const CHECKBOX_COL_NULL = { isCheckboxCol: true } as ColumnFilter;

interface InlineFilterRowProps {
    /** Column definitions — one per <th>. Use `null` for columns that shouldn't have a filter (e.g. checkbox, actions) */
    columns?: (ColumnFilter | null)[];
    /** All available filter field definitions */
    filterFields?: (FilterField | any)[];
    /** Current filter values */
    filterValues?: ActiveFilters;
    /** Callback when a filter value changes */
    onFilterChange?: (filters: ActiveFilters) => void;

    // Backward / convenient aliases:
    fields?: (FilterField | any)[];
    values?: ActiveFilters;
    onChange?: (keyOrFilters: any, val?: any) => void;
    className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * InlineFilterRow — Renders a <tr> with filter inputs below the table header row.
 *
 * Usage in a list page:
 * ```tsx
 * <thead>
 *   <tr>...</tr>  <!-- Column headers -->
 *   <InlineFilterRow columns={[...]} filterFields={FILTER_FIELDS} ... />
 * </thead>
 * ```
 */
export function InlineFilterRow({
    columns: explicitColumns,
    filterFields: explicitFilterFields,
    filterValues: explicitFilterValues,
    onFilterChange,
    fields,
    values,
    onChange,
    className,
}: InlineFilterRowProps) {
    const filterFields: any[] = explicitFilterFields || fields || [];
    const filterValues: ActiveFilters = explicitFilterValues || values || {};

    const columns: (ColumnFilter | null)[] = explicitColumns && explicitColumns.length > 0
        ? explicitColumns
        : [...filterFields.map(f => ({ key: f.key || f.id })), null];

    const setField = (key: string, val: string | string[] | { from?: string; to?: string }) => {
        if (onFilterChange) {
            onFilterChange({ ...filterValues, [key]: val });
        } else if (onChange) {
            onChange(key, val);
        }
    };

    return (
        <tr className={cn("apv-filter-row bg-slate-50/60 border-b border-slate-200", className)}>
            {columns.map((col, idx) => {
                // Checkbox col sentinel — render with same CSS class as header
                if (col && (col as any).isCheckboxCol) {
                    return <th key={idx} className="apv-col-checkbox" />;
                }

                const isStickyLeft = idx === 1 && (columns[0] as any)?.isCheckboxCol;
                const isStickyRight = idx === columns.length - 1;
                const stickyClass = cn(
                    isStickyLeft && "apv-col-code",
                    isStickyRight && "apv-col-actions"
                );

                if (!col) {
                    return <th key={idx} className={cn("px-1 py-1", stickyClass)} />;
                }

                const field = filterFields.find(f => (f.key || f.id) === col.key);
                if (!field) {
                    return <th key={idx} className={cn("px-1 py-1", stickyClass)} />;
                }

                const fieldKey = field.key || field.id;
                return (
                    <th key={col.key || idx} className={cn("px-1 py-1 font-normal", stickyClass)} style={col.width ? { width: col.width } : undefined}>
                        <FilterCellInput
                            field={field}
                            value={filterValues[fieldKey]}
                            onChange={val => setField(fieldKey, val)}
                        />
                    </th>
                );
            })}
        </tr>
    );
}

// ── Filter Cell Input ─────────────────────────────────────────────────────────

function FilterCellInput({
    field,
    value,
    onChange,
}: {
    field: FilterField;
    value: string | string[] | { from?: string; to?: string } | undefined;
    onChange: (v: string | string[] | { from?: string; to?: string }) => void;
}) {
    if (field.type === 'select') {
        const v = typeof value === 'string' ? value : '';
        return (
            <SearchableFilterDropdown
                options={field.options || []}
                value={v}
                onChange={(nextVal) => onChange(typeof nextVal === 'string' ? nextVal : (nextVal[0] || ''))}
                placeholder={field.placeholder || 'Tất cả'}
            />
        );
    }

    if (field.type === 'combobox-multi') {
        const v = Array.isArray(value) ? value : (typeof value === 'string' && value ? [value] : []);
        return (
            <SearchableFilterDropdown
                options={field.options || []}
                value={v}
                isMulti
                onChange={(nextVal) => onChange(nextVal)}
                placeholder={field.placeholder || 'Tất cả'}
            />
        );
    }

    if (field.type === 'date-range') {
        const dv = typeof value === 'object' && value !== null ? value as any : {};
        const from = dv?.from || '';
        const to = dv?.to || '';

        return (
            <DateRangeInput from={from} to={to} onChange={onChange} />
        );
    }

    // type = 'text'
    const v = typeof value === 'string' ? value : '';
    return (
        <Input
            value={v}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder || '🔍'}
            className="h-6 text-[10px] w-full border-slate-200 bg-white shadow-none px-1.5 focus:ring-1 focus:ring-indigo-300"
        />
    );
}

// ── Date Range Input (compact for inline use) ─────────────────────────────────

function DateRangeInput({
    from,
    to,
    onChange,
}: {
    from: string;
    to: string;
    onChange: (v: { from?: string; to?: string }) => void;
}) {
    const [showInputs, setShowInputs] = useState(!!(from || to));

    if (!showInputs) {
        return (
            <button
                onClick={() => setShowInputs(true)}
                className="h-6 w-full flex items-center justify-center gap-1 text-[10px] text-slate-400 hover:text-indigo-600 border border-dashed border-slate-200 rounded-md bg-white hover:border-indigo-300 transition-colors"
            >
                <Calendar className="h-3 w-3" />
                Chọn ngày
            </button>
        );
    }

    return (
        <div className="flex items-center gap-0.5">
            <Input
                type="date"
                value={from}
                max={to || undefined}
                onChange={e => onChange({ from: e.target.value, to })}
                className="h-6 text-[10px] flex-1 min-w-0 border-slate-200 bg-white shadow-none px-1 focus:ring-1 focus:ring-indigo-300"
            />
            <span className="text-[9px] text-slate-400 shrink-0">–</span>
            <Input
                type="date"
                value={to}
                min={from || undefined}
                onChange={e => onChange({ from, to: e.target.value })}
                className="h-6 text-[10px] flex-1 min-w-0 border-slate-200 bg-white shadow-none px-1 focus:ring-1 focus:ring-indigo-300"
            />
        </div>
    );
}
